import { BankIdentifierValidationType } from '@dealstack/shared/lib/generated';
import type { BankAccountVerificationFailureKind } from '../../BankAccountFormContext.types.ts';

// Only what this module asks of a translator: a key plus a default and optional
// interpolation. Narrower than the branded TFunction, so the real `t` satisfies it
// and a test can pass a plain function without casting.
type Translate = (key: string, options: { defaultValue: string; identifier?: string }) => string;

// What each validation type actually checks, named for use inside a sentence.
// defaultValue keeps the copy readable before the keys land in Tolgee.
const IDENTIFIER_NAMES: Record<
	BankIdentifierValidationType,
	{ key: string; defaultValue: string }
> = {
	[BankIdentifierValidationType.NONE]: {
		key: 'bank_account.drawer.verification_identifier.generic',
		defaultValue: 'bank details',
	},
	[BankIdentifierValidationType.IBAN]: {
		key: 'bank_account.drawer.verification_identifier.iban',
		defaultValue: 'IBAN',
	},
	[BankIdentifierValidationType.BIC]: {
		key: 'bank_account.drawer.verification_identifier.bic',
		defaultValue: 'SWIFT/BIC code',
	},
	[BankIdentifierValidationType.ABA_ROUTING]: {
		key: 'bank_account.drawer.verification_identifier.aba_routing',
		defaultValue: 'routing number',
	},
	[BankIdentifierValidationType.CA_ROUTING]: {
		key: 'bank_account.drawer.verification_identifier.ca_routing',
		defaultValue: 'institution and transit numbers',
	},
	[BankIdentifierValidationType.SORT_CODE]: {
		key: 'bank_account.drawer.verification_identifier.sort_code',
		defaultValue: 'sort code',
	},
};

const FAILURE_COPY: Record<
	BankAccountVerificationFailureKind,
	{ title: { key: string; defaultValue: string }; text: { key: string; defaultValue: string } }
> = {
	invalid: {
		title: {
			key: 'bank_account.drawer.verification_failed.invalid_title',
			defaultValue: 'Bank details not found',
		},
		text: {
			key: 'bank_account.drawer.verification_failed.invalid_text',
			defaultValue:
				"We couldn't find a bank matching the {{identifier}} you entered. Check it and try again.",
		},
	},
	unavailable: {
		title: {
			key: 'bank_account.drawer.verification_failed.unavailable_title',
			defaultValue: 'Verification unavailable',
		},
		text: {
			key: 'bank_account.drawer.verification_failed.unavailable_text',
			defaultValue:
				"We couldn't verify the {{identifier}} right now. You can save these details — they will be checked during review.",
		},
	},
};

export const resolveVerificationFailureCopy = (
	t: Translate,
	validationType: BankIdentifierValidationType,
	failureKind: BankAccountVerificationFailureKind
): { title: string; text: string } => {
	const identifierName = IDENTIFIER_NAMES[validationType] ?? IDENTIFIER_NAMES.NONE;
	const identifier = t(identifierName.key, { defaultValue: identifierName.defaultValue });
	const copy = FAILURE_COPY[failureKind];

	return {
		title: t(copy.title.key, { defaultValue: copy.title.defaultValue }),
		text: t(copy.text.key, { defaultValue: copy.text.defaultValue, identifier }),
	};
};
